import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, Check, Sparkles, ArrowDown } from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

export const ToolkitPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleWaitlistSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'toolkit-waitlist' })
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO {...pageSEO.toolkit} path="/toolkit" />
      {/* Hero Section - Full Height like Homepage */}
      <section className="relative h-screen min-h-[700px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1601128688653-7dc405e3ac4d?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-charcoal)] via-[var(--color-charcoal)]/85 to-[var(--color-charcoal)]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-transparent to-[var(--color-charcoal)]/30" />
        </div>
        
        {/* Content - Left Aligned like Homepage */}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="max-w-4xl"
          >
            {/* Coming Soon Badge */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-8"
            >
              <span className="inline-flex items-center gap-3 px-6 py-3 bg-[var(--color-gold)] rounded-full shadow-lg shadow-[var(--color-gold)]/20">
                <Sparkles className="w-5 h-5 text-[var(--color-charcoal)]" />
                <span className="text-[var(--color-charcoal)] font-bold text-sm tracking-[0.2em] uppercase">
                  Coming Soon
                </span>
              </span>
            </motion.div>

            {/* Main Title - Large like Homepage */}
            <motion.h1 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="font-serif text-5xl md:text-7xl lg:text-8xl mb-6 leading-[0.9] tracking-tight"
            >
              <span className="text-[var(--color-cream)]">The Soul Script</span>
              <br />
              <span className="text-[var(--color-gold)]">Self Care Toolkit</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mt-8"
            >
              <p className="text-[var(--color-gold)] text-lg md:text-xl tracking-[0.2em] uppercase mb-4">
                A Self-Guided Resource for Seekers
              </p>
              <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 max-w-2xl leading-relaxed">
                Something is being crafted with care—a companion for those who want to sit with themselves more honestly.
              </p>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.3 }}
              className="mt-12"
            >
              <button
                onClick={scrollToWaitlist}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 group"
              >
                Join the Waitlist
                <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-[var(--color-cream)]/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-2 bg-[var(--color-gold)] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* About the Toolkit Section */}
      <section className="py-24 lg:py-32 bg-[var(--color-charcoal)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-12 leading-tight text-[var(--color-cream)]">
              A grounded resource for those who value depth, clarity, and personal responsibility in their inner work.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <p className="text-lg md:text-xl text-[var(--color-cream)]/75 leading-[1.9] text-center">
              This toolkit is being designed as a companion for seekers who want tools to practise reflection independently. It will support emotional resilience, self-acceptance, and the courage to meet yourself with honesty—without dependency on external guidance.
            </p>
            <p className="text-lg md:text-xl text-[var(--color-cream)]/75 leading-[1.9] text-center">
              Details will be shared when it's ready. For now, join the waitlist to be the first to know.
            </p>
          </motion.div>

          {/* Decorative Divider */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 pt-12 border-t border-[var(--color-dark-purple)]/30"
          >
            <div className="text-center">
              <p className="text-xl md:text-2xl leading-relaxed text-[var(--color-cream)]/60 mb-2">
                This is not a course or coaching substitute.
              </p>
              <p className="text-xl md:text-2xl leading-relaxed text-[var(--color-gold)] font-serif">
                It is a quiet companion for the lifelong work of knowing yourself.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist" className="py-24 lg:py-32 bg-[var(--color-dark-purple)]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
              Be the First to Know
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              Join the Waitlist
            </h2>
            <p className="text-lg text-[var(--color-cream)]/60 mb-10 leading-relaxed max-w-xl mx-auto">
              Enter your email to be notified when The Soul Script Self Care Toolkit is ready.
            </p>
            
            {submitStatus === 'success' ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 bg-[var(--color-bottle-green)]/20 rounded-lg border border-[var(--color-bottle-green)]/30 max-w-md mx-auto"
              >
                <Check className="w-10 h-10 text-[var(--color-bottle-green-light)] mx-auto mb-4" />
                <p className="text-[var(--color-cream)] font-serif text-xl mb-2">You're on the waitlist</p>
                <p className="text-[var(--color-cream)]/60">
                  We'll notify you when it's ready.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleWaitlistSignup} className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="flex-1 px-6 py-4 bg-[var(--color-charcoal)] border border-[var(--color-dark-purple)]/50 rounded-full text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                    data-testid="toolkit-waitlist-email"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    data-testid="toolkit-waitlist-submit"
                  >
                    <Bell className="w-4 h-4" />
                    {isSubmitting ? 'Joining...' : 'Notify Me'}
                  </button>
                </div>
                {submitStatus === 'error' && (
                  <p className="text-[var(--color-deep-red-light)] text-sm mt-4">
                    Something went wrong. Please try again.
                  </p>
                )}
                <p className="text-xs text-[var(--color-cream)]/40 mt-4">
                  Your email is safe. No spam, ever.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Closing Quote */}
      <section className="py-16 bg-[var(--color-charcoal)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-serif text-xl md:text-2xl text-[var(--color-cream)]/50 italic leading-relaxed"
          >
            "The work of knowing yourself is lifelong. When it's ready, this toolkit will be a quiet companion for that journey."
          </motion.p>
        </div>
      </section>
    </div>
  );
};
